import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import {
  type IMarketingConsentCampaignEligibleCustomer,
  type IMarketingConsentCampaignSentCustomer,
  type IMarketingConsentCampaignSentSummary,
  type IMarketingConsentCampaignState,
} from './types';

export const preloadedState: IMarketingConsentCampaignState = {
  ...asyncStateInitial,
  sent: undefined,
  skipped: undefined,
  eligibleCustomers: undefined,
  totalCount: undefined,
  sentCustomers: undefined,
  sentSummary: undefined,
};

const marketingConsentCampaignSlice = createSlice({
  name: 'MarketingConsentCampaign',
  initialState: preloadedState,
  reducers: {
    marketingConsentCampaignSend: {
      reducer(state) {
        startLoading(state);
      },
      prepare(customerUuids: string[]) {
        return { payload: { customerUuids } };
      },
    },
    marketingConsentCampaignSendResponse: {
      reducer(
        state,
        action: { payload: { sent: string[]; skipped: string[] } },
      ) {
        state.sent = action.payload.sent;
        state.skipped = action.payload.skipped;
        finishLoading(state);
      },
      prepare(sent: string[], skipped: string[]) {
        return { payload: { sent, skipped } };
      },
    },
    marketingConsentCampaignEligibleList: {
      reducer(state) {
        startLoading(state);
      },
      prepare(criteria: {
        countryTypeIsoCode?: string;
        orderStatusCodes?: string[];
      }) {
        return { payload: criteria };
      },
    },
    marketingConsentCampaignEligibleListResponse: {
      reducer(
        state,
        action: {
          payload: {
            customers: IMarketingConsentCampaignEligibleCustomer[];
            totalCount: number;
          };
        },
      ) {
        state.eligibleCustomers = action.payload.customers;
        state.totalCount = action.payload.totalCount;
        finishLoading(state);
      },
      prepare(
        customers: IMarketingConsentCampaignEligibleCustomer[],
        totalCount: number,
      ) {
        return { payload: { customers, totalCount } };
      },
    },
    marketingConsentCampaignSentList: {
      reducer(state) {
        startLoading(state);
      },
      prepare() {
        return { payload: {} };
      },
    },
    marketingConsentCampaignSentListResponse: {
      reducer(
        state,
        action: {
          payload: {
            sends: IMarketingConsentCampaignSentCustomer[];
            summary: IMarketingConsentCampaignSentSummary;
          };
        },
      ) {
        state.sentCustomers = action.payload.sends;
        state.sentSummary = action.payload.summary;
        finishLoading(state);
      },
      prepare(
        sends: IMarketingConsentCampaignSentCustomer[],
        summary: IMarketingConsentCampaignSentSummary,
      ) {
        return { payload: { sends, summary } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('MarketingConsentCampaign'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  marketingConsentCampaignSend,
  marketingConsentCampaignSendResponse,
  marketingConsentCampaignEligibleList,
  marketingConsentCampaignEligibleListResponse,
  marketingConsentCampaignSentList,
  marketingConsentCampaignSentListResponse,
} = marketingConsentCampaignSlice.actions;
export default marketingConsentCampaignSlice.reducer;
